import React from "react";
import PropTypes from "prop-types";
import {
  useTooltip,
  useTooltipTrigger,
  OverlayContainer,
  useOverlayPosition,
  mergeProps,
} from "react-aria";
import { useTooltipTriggerState } from "react-stately";
import { FocusableProvider } from "@react-aria/focus";
import { AnimatePresence, motion } from "framer-motion";

const missingProvider = "Component must be used within <Tooltip.Root>";
const TooltipContext = React.createContext({
  get triggerRef() {
    throw new Error(missingProvider);
  },
  get tooltipProps() {
    throw new Error(missingProvider);
  },
  get tooltipState() {
    throw new Error(missingProvider);
  },
});
TooltipContext.displayName = "TooltipContext";

function ContentInner({ offset, crossOffset, placement, children, className }) {
  const overlayRef = React.useRef(null);

  const {
    tooltipState,
    triggerRef,
    tooltipProps: triggerTooltipProps,
  } = React.useContext(TooltipContext);

  const { overlayProps } = useOverlayPosition({
    placement,
    targetRef: triggerRef,
    overlayRef,
    isOpen: tooltipState.isOpen,
    containerPadding: 0,
    offset,
    crossOffset,
  });

  const { tooltipProps } = useTooltip(
    { isOpen: tooltipState.isOpen, ...overlayProps },
    tooltipState,
  );

  return (
    <motion.div
      {...mergeProps(triggerTooltipProps, tooltipProps)}
      ref={overlayRef}
      style={overlayProps.style}
      className={className}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { duration: 0.2, ease: "easeOut" },
      }}
      exit={{
        opacity: 0,
        transition: { duration: 0.15, ease: "easeIn" },
      }}
    >
      {children}
    </motion.div>
  );
}

export function Content({
  offset,
  crossOffset,
  placement,
  children,
  className,
}) {
  const { tooltipState } = React.useContext(TooltipContext);

  return (
    <AnimatePresence>
      {tooltipState.isOpen ? (
        <OverlayContainer>
          <ContentInner
            offset={offset}
            crossOffset={crossOffset}
            placement={placement}
            className={className}
          >
            {children}
          </ContentInner>
        </OverlayContainer>
      ) : null}
    </AnimatePresence>
  );
}

export function Root({ isDisabled, children }) {
  const ref = React.useRef(null);
  const tooltipState = useTooltipTriggerState({ isDisabled });
  const { triggerProps, tooltipProps } = useTooltipTrigger(
    { isDisabled },
    tooltipState,
    ref,
  );

  return (
    <TooltipContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{ triggerRef: ref, tooltipProps, tooltipState }}
    >
      <FocusableProvider {...triggerProps} ref={ref}>
        {children}
      </FocusableProvider>
    </TooltipContext.Provider>
  );
}

Root.propTypes = {
  isDisabled: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};

Content.propTypes = {
  offset: PropTypes.number,
  crossOffset: PropTypes.number,
  placement: PropTypes.oneOf(["bottom", "top", "left", "right"]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};

ContentInner.propTypes = Content.propTypes;

Root.defaultProps = {
  isDisabled: false,
  className: undefined,
};

Content.defaultProps = {
  offset: 0,
  crossOffset: 0,
  placement: "bottom",
  className: undefined,
};

ContentInner.defaultProps = Content.defaultProps;
